import moment from 'moment';

const FIXED_PRICES = 'pricesFixedForm';
const CATEGORY_PRICES = 'pricesCategoryForm';
const DAILY_PRICES = 'pricesDailyForm';
const FUTURE_PRICES = 'futurePricesForm';

const getParticipants = (
  accommodation,
  currency,
  previousParticipants,
  dates
) => {
  let participants = [];

  if (accommodation.pricesType === FIXED_PRICES) {
    const prices = accommodation[FIXED_PRICES];
    participants.push({
      id: `${currency}-${prices[currency]}`,
      participant: '',
      participants: 0,
      currency: currency,
      currencyValue: prices[currency],
      NOK: prices.NOK,
      EUR: prices.EUR,
      USD: prices.USD
    });
    participants = _setPreviousParticipantValue(
      participants,
      previousParticipants
    );
    return participants;
  }

  if (
    accommodation.pricesType === FUTURE_PRICES &&
    dates?.startDate &&
    dates?.endDate
  ) {
    const start = moment(dates.startDate);
    const end = moment(dates.endDate);
    const futurePrices = accommodation[FUTURE_PRICES] || [];

    let hasMatchingFuturePrice = false;

    futurePrices.forEach(priceSet => {
      const priceStart = moment(priceSet.startDate);
      const priceEnd = moment(priceSet.endDate);

      const isMatching =
        start.isBetween(priceStart, priceEnd, null, '[]') ||
        end.isBetween(priceStart, priceEnd, null, '[]') ||
        (start.isSameOrBefore(priceStart) && end.isSameOrAfter(priceEnd));

      if (isMatching) {
        hasMatchingFuturePrice = true;
        const priceValue = priceSet.prices[currency];

        participants.push({
          id: `${currency}-${priceValue}`,
          participant: '',
          participants: 0,
          currency,
          currencyValue: priceValue,
          NOK: priceSet.prices.NOK,
          EUR: priceSet.prices.EUR,
          USD: priceSet.prices.USD,
          ratePlanId: priceSet.ratePlanId,
          ratePlanTitle: priceSet.ratePlanTitle
        });
      }
    });

    if (!hasMatchingFuturePrice) {
      const otherPrices = [];

      let dailyPriceAvailable = false;

      // Fallback: Daily Prices
      if (
        dates?.startDate &&
        dates?.endDate &&
        accommodation?.pricesDailyForm?.days?.length > 0
      ) {
        const start = moment(dates.startDate);
        const end = moment(dates.endDate).subtract(1, 'd');
        let value = 0;
        const dailyPrices = accommodation.pricesDailyForm.days;

        for (
          let date = moment(start);
          date.isSameOrBefore(end);
          date.add(1, 'days')
        ) {
          const day = date.format('ddd');
          dailyPrices.forEach(dailyPrice => {
            if (dailyPrice.day === day && dailyPrice[currency]) {
              value += +dailyPrice[currency];
            }
          });
        }

        if (value > 0) {
          dailyPriceAvailable = true;
          otherPrices.push({
            id: `${currency}-${value}`,
            participant: '',
            participants: 0,
            currency,
            currencyValue: value,
            NOK: currency === 'NOK' ? value : 0,
            EUR: currency === 'EUR' ? value : 0,
            USD: currency === 'USD' ? value : 0,
            title: 'Other Prices'
          });
        }
      }

      // Fallback: Category Prices only if daily prices not found
      if (!dailyPriceAvailable) {
        const categories = accommodation[CATEGORY_PRICES]?.categories || [];
        categories.forEach(category => {
          const priceValue = category[currency];
          if (priceValue && Number(priceValue) > 0) {
            otherPrices.push({
              id: `${currency}-${priceValue}`,
              participant: category.participant,
              participants: 0,
              currency,
              currencyValue: priceValue,
              NOK: category.NOK,
              EUR: category.EUR,
              USD: category.USD,
              title: 'Other Prices'
            });
          }
        });
      }

      //  Fallback 3: Fixed Prices
      const fixed = accommodation[FIXED_PRICES];
      if (otherPrices.length === 0 && fixed && Number(fixed[currency]) > 0) {
        otherPrices.push({
          id: `${currency}-${fixed[currency]}`,
          participant: '',
          participants: 0,
          currency,
          currencyValue: fixed[currency],
          NOK: fixed.NOK,
          EUR: fixed.EUR,
          USD: fixed.USD,
          title: 'Other Prices'
        });
      }

      participants = [...participants, ...otherPrices];
    }

    participants = _setPreviousParticipantValue(
      participants,
      previousParticipants
    );

    return participants;
  }

  if (accommodation.pricesType === DAILY_PRICES) {
    if (dates && dates.startDate && dates.endDate) {
      const dailyPrices = accommodation.pricesDailyForm.days;
      const start = dates.startDate;
      const end = moment(dates.endDate).subtract(1, 'd');
      let value = 0;

      for (
        let date = moment(start);
        date.isSameOrBefore(end);
        date.add(1, 'days')
      ) {
        const day = date.format('ddd');
        dailyPrices.forEach(dailyPrice => {
          if (dailyPrice.day === day && dailyPrice[currency]) {
            value += +dailyPrice[currency];
          }
        });
      }

      participants.push({
        id: `${currency}-${value}`,
        participant: '',
        participants: 0,
        currency: currency,
        currencyValue: value,
        NOK: currency === 'NOK' ? value : 0,
        EUR: currency === 'EUR' ? value : 0,
        USD: currency === 'USD' ? value : 0
      });
    }
  } else {
    const categories = accommodation[CATEGORY_PRICES].categories;
    categories.forEach(item => {
      participants.push({
        id: `${item.participant}-${currency}-${item[currency]}`,
        participant: item.participant,
        participants: 0,
        currency: currency,
        currencyValue: item[currency],
        NOK: item.NOK,
        EUR: item.EUR,
        USD: item.USD
      });
    });
  }

  participants = _setPreviousParticipantValue(
    participants,
    previousParticipants
  );

  return participants;
};

const _setPreviousParticipantValue = (participants, previousParticipants) => {
  return participants.map(item => {
    const theSameParticipant = previousParticipants
      ? previousParticipants.find(
          participant => participant.participant === item.participant
        )
      : false;
    if (theSameParticipant) {
      return {
        ...item,
        participants: theSameParticipant.participants
      };
    }
    return item;
  });
};

export { getParticipants };
